<template>
  <b-modal
    ref="ruleModal"
    :title="modalTitle"
    size="lg"
    hide-footer
    @hidden="resetModal"
    body-class="p-0"
  >
    <div class="rule-modal-content">

      <!-- Modal Body -->
      <div class="modal-body-custom">
        <!-- Error Alert -->
        <div v-if="error" class="alert alert-danger">
          <i class="las la-exclamation-triangle"></i>
          {{ error }}
        </div>

        <!-- Success Alert -->
        <div v-if="successMessage" class="alert alert-success">
          <i class="las la-check-circle"></i>
          {{ successMessage }}
        </div>

        <!-- Form -->
        <form @submit.prevent="saveRule">
          <!-- Name Field -->
          <div class="form-group">
            <label for="ruleName" class="form-label required">
              <i class="las la-tag"></i>
              Rule Name
            </label>
            <input
              id="ruleName"
              type="text"
              class="form-control compact-select"
              v-model="formData.name"
              :class="{ 'is-invalid': validationErrors.name }"
              placeholder="Enter rule name"
              :disabled="saving"
              required
            />
            <div v-if="validationErrors.name" class="invalid-feedback">
              {{ validationErrors.name }}
            </div>
          </div>

          <!-- Description Field -->
          <div class="form-group">
            <label for="ruleDescription" class="form-label">
              <i class="las la-align-left"></i>
              Description
            </label>
            <textarea
              id="ruleDescription"
              class="form-control compact-select"
              v-model="formData.description"
              :class="{ 'is-invalid': validationErrors.description }"
              placeholder="Enter rule description (optional)"
              rows="1"
              :disabled="saving"
            ></textarea>
            <div v-if="validationErrors.description" class="invalid-feedback">
              {{ validationErrors.description }}
            </div>
          </div>

          <!-- Rule Logic Fields -->
          <div class="form-group">
            <label class="form-label required">
              <i class="las la-code"></i>
              Rule
            </label>
            
            <div class="rule-logic-container">
              <div class="rule-mode-block">
                <div class="rule-mode-row">
                  <label for="ruleMode" class="form-label-sm required rule-mode-label">
                    Rule Mode
                  </label>
                  <select
                    id="ruleMode"
                    class="form-control compact-select rule-mode-select"
                    v-model="ruleLogic.mode"
                    :class="{ 'is-invalid': validationErrors.mode, 'select-open': ruleModeOpen }"
                    :disabled="saving"
                    @focus="ruleModeOpen = true"
                    @blur="ruleModeOpen = false"
                    required
                  >
                    <option value="prior_window">Prior Window</option>
                    <option value="historical_window">Historical Window</option>
                  </select>
                </div>
                <div v-if="validationErrors.mode" class="invalid-feedback d-block rule-mode-inline-error">
                  {{ validationErrors.mode }}
                </div>
              </div>

              <!-- Window Field -->
              <div class="row">
                <div class="col-md-3">
                  <label for="ruleWindow" class="form-label-sm required">
                    Window (days)
                  </label>
                  <input
                    id="ruleWindow"
                    type="number"
                    class="form-control compact-select"
                    v-model.number="ruleLogic.window"
                    :class="{ 'is-invalid': validationErrors.window }"
                    placeholder="e.g., 30"
                    :disabled="saving"
                    min="1"
                    required
                  />
                  <div v-if="validationErrors.window" class="invalid-feedback">
                    {{ validationErrors.window }}
                  </div>
                </div>
                
                <!-- Compare To Field -->
                <div class="col-md-3">
                  <template v-if="ruleLogic.mode === 'prior_window'">
                    <label for="ruleCompareTo" class="form-label-sm required">
                      Compare To (Past Days)
                    </label>
                    <input
                      id="ruleCompareTo"
                      type="number"
                      class="form-control compact-select"
                      v-model.number="ruleLogic.compare_to"
                      :class="{ 'is-invalid': validationErrors.compare_to }"
                      placeholder="e.g., 100"
                      :disabled="saving"
                      min="1"
                      required
                    />
                  </template>
                  <template v-else>
                    <label for="ruleWindowOffsetAmount" class="form-label-sm required">
                      Window offset
                    </label>
                    <div class="d-flex align-items-stretch window-offset-row">
                      <input
                        id="ruleWindowOffsetAmount"
                        type="number"
                        class="form-control compact-select window-offset-value-input"
                        v-model.number="ruleLogic.window_offset_amount"
                        :class="{ 'is-invalid': validationErrors.window_offset }"
                        placeholder="e.g., 1"
                        :disabled="saving"
                        min="1"
                        required
                      />
                      <select
                        id="ruleWindowOffsetUnit"
                        class="form-control compact-select window-offset-unit-select"
                        v-model="ruleLogic.window_offset_unit"
                        :class="{ 'is-invalid': validationErrors.window_offset, 'select-open': windowOffsetUnitOpen }"
                        :disabled="saving"
                        @focus="windowOffsetUnitOpen = true"
                        @blur="windowOffsetUnitOpen = false"
                        required
                      >
                        <option value="months">Months</option>
                        <option value="years">Years</option>
                      </select>
                    </div>
                    <div v-if="validationErrors.window_offset" class="invalid-feedback d-block">
                      {{ validationErrors.window_offset }}
                    </div>
                  </template>
                  <div
                    v-if="ruleLogic.mode === 'prior_window' && validationErrors.compare_to"
                    class="invalid-feedback"
                  >
                    {{ validationErrors.compare_to }}
                  </div>
                </div>
                
                <!-- Aggregator Field -->
                <div class="col-md-3">
                  <label for="ruleAggregator" class="form-label-sm required">
                    Aggregator
                  </label>
                  <select
                    id="ruleAggregator"
                    class="form-control compact-select"
                    v-model="ruleLogic.aggregator"
                    :class="{ 'is-invalid': validationErrors.aggregator, 'select-open': aggregatorOpen }"
                    :disabled="saving"
                    @focus="aggregatorOpen = true"
                    @blur="aggregatorOpen = false"
                    required
                  >
                    <option value="">Select aggregator</option>
                    <option value="sum">Sum</option>
                    <option value="avg">Average</option>
                    <option value="count">Count</option>
                    <option value="min">Min</option>
                    <option value="max">Max</option>
                    <option value="distinct_count">Distinct Count</option>
                  </select>
                  <div v-if="validationErrors.aggregator" class="invalid-feedback">
                    {{ validationErrors.aggregator }}
                  </div>
                </div>
                
                <!-- Operator Field -->
                <div class="col-md-3">
                  <label for="ruleOperator" class="form-label-sm required">
                    Operator
                  </label>
                  <select
                    id="ruleOperator"
                    class="form-control compact-select"
                    v-model="ruleLogic.operator"
                    :class="{ 'is-invalid': validationErrors.operator, 'select-open': operatorOpen }"
                    :disabled="saving"
                    @focus="operatorOpen = true"
                    @blur="operatorOpen = false"
                    required
                  >
                    <option value="">Select operator</option>
                    <option value="decrease_pct">Decrease %</option>
                    <option value="increase_pct">Increase %</option>
                    <option value="equal">Equal</option>
                  </select>
                  <div v-if="validationErrors.operator" class="invalid-feedback">
                    {{ validationErrors.operator }}
                  </div>
                </div>
              </div>
            </div>
            
            <div v-if="validationErrors.rule" class="invalid-feedback d-block">
              {{ validationErrors.rule }}
            </div>
            <small class="form-text text-muted">
              Prior Window uses day-based window and compare-to; Historical Window adds a window offset as months or years only.
            </small>
          </div>

          <!-- Query Template Field -->
          <div class="form-group">
            <label for="queryTemplate" class="form-label required">
              <i class="las la-search"></i>
              Query
            </label>
            <textarea
              id="queryTemplate"
              class="form-control compact-select"
              v-model="formData.query_template"
              :class="{ 'is-invalid': validationErrors.query_template }"
              placeholder="Enter query template"
              rows="3"
              :disabled="saving"
              required
            ></textarea>
            <div v-if="validationErrors.query_template" class="invalid-feedback">
              {{ validationErrors.query_template }}
            </div>
            <small class="form-text text-muted">
              SQL or query template for data retrieval
            </small>
          </div>

          <!-- Threshold Field -->
          <div class="form-group">
            <label for="ruleThreshold" class="form-label required">
              <i class="las la-sliders-h"></i>
              Threshold
            </label>
            <input
              id="ruleThreshold"
              type="text"
              class="form-control compact-select"
              v-model="formData.threshold"
              :class="{ 'is-invalid': validationErrors.threshold }"
              placeholder="Enter threshold value"
              :disabled="saving"
              required
            />
            <div v-if="validationErrors.threshold" class="invalid-feedback">
              {{ validationErrors.threshold }}
            </div>
            <small class="form-text text-muted">
              Threshold value for rule evaluation
            </small>
          </div>

          <!-- Active Status Field -->
          <div class="form-group">
            <div class="custom-control custom-switch">
              <input
                type="checkbox"
                class="custom-control-input"
                id="ruleActive"
                v-model="formData.is_active"
                :disabled="saving"
              />
              <label class="custom-control-label" for="ruleActive">
                Rule is Active
              </label>
            </div>
            <small class="form-text text-muted">
              Only active rules will be evaluated
            </small>
          </div>
        </form>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer-custom">
        <button
          type="button"
          class="btn btn-secondary"
          @click="closeModal"
          :disabled="saving"
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          @click="saveRule"
          :disabled="saving || !isFormValid"
        >
          <span v-if="saving" class="spinner-border spinner-border-sm mr-2"></span>
          <i v-else class="las la-save mr-1"></i>
          {{ saving ? 'Saving...' : (editingRule ? 'Update Rule' : 'Create Rule') }}
        </button>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <b-modal
      ref="deleteModal"
      title="Confirm Delete"
      size="sm"
      hide-footer
      @hidden="resetDeleteModal"
    >
      <div class="text-center">
        <i class="las la-exclamation-triangle text-warning la-3x mb-3"></i>
        <p>Are you sure you want to delete this rule?</p>
        <p class="text-muted">
          <strong>{{ formData.name }}</strong>
        </p>
        <p class="text-danger">This action cannot be undone.</p>
      </div>
      
      <div class="d-flex justify-content-center gap-2">
        <button
          type="button"
          class="btn btn-secondary"
          @click="cancelDelete"
          :disabled="deleting"
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-danger"
          @click="deleteRule"
          :disabled="deleting"
        >
          <span v-if="deleting" class="spinner-border spinner-border-sm mr-2"></span>
          {{ deleting ? 'Deleting...' : 'Delete Rule' }}
        </button>
      </div>
    </b-modal>
  </b-modal>
</template>

<script>
import RulesService from "@/services/rules.service";
import { formatErrorMessage } from "@/utils/random_utils";

export default {
  name: "RuleModal",
  data() {
    return {
      editingRule: null,
      saving: false,
      deleting: false,
      error: null,
      successMessage: null,
      validationErrors: {},
      
      formData: {
        name: '',
        description: '',
        rule: '',
        query_template: '',
        threshold: '',
        is_active: true
      },
      
      ruleLogic: {
        mode: 'prior_window',
        window: null,
        compare_to: null,
        window_offset_amount: null,
        window_offset_unit: 'months',
        operator: '',
        aggregator: ''
      },
      
      ruleModeOpen: false,
      windowOffsetUnitOpen: false,
      aggregatorOpen: false,
      operatorOpen: false,

      originalData: null
    };
  },
  
  computed: {
    modalTitle() {
      return this.editingRule ? 'Edit Rule' : 'Add New Rule';
    },
    
    isFormValid() {
      const windowOk =
        this.ruleLogic.window &&
        Number(this.ruleLogic.window) > 0;

      let horizonOk = false;
      if (this.ruleLogic.mode === 'prior_window') {
        horizonOk =
          this.ruleLogic.compare_to !== null &&
          this.ruleLogic.compare_to !== '' &&
          Number(this.ruleLogic.compare_to) > 0;
      } else {
        horizonOk =
          this.ruleLogic.window_offset_amount !== null &&
          this.ruleLogic.window_offset_amount !== '' &&
          Number(this.ruleLogic.window_offset_amount) > 0 &&
          ['months', 'years'].includes(this.ruleLogic.window_offset_unit);
      }

      return (
        this.formData.name.trim() &&
        windowOk &&
        horizonOk &&
        this.ruleLogic.operator &&
        this.ruleLogic.aggregator &&
        this.formData.query_template.trim() &&
        String(this.formData.threshold).trim()
      );
    }
  },
  
  methods: {
    openModal(rule = null) {
      this.editingRule = rule;
      
      if (rule) {
        // Edit mode - populate form with rule data
        this.formData = {
          name: rule.name || '',
          description: rule.description || '',
          rule: rule.rule || '',
          query_template: rule.query_template || '',
          threshold: rule.threshold ? String(rule.threshold) : '',
          is_active: rule.is_active !== false
        };
        
        // Parse rule logic if it exists
        if (rule.rule) {
          try {
            const parsedRule = typeof rule.rule === 'string' ? JSON.parse(rule.rule) : rule.rule;
            this.ruleLogic = this.parseLoadedRuleLogic(parsedRule);
          } catch (error) {
            console.warn('Failed to parse rule logic:', error);
            this.resetRuleLogic();
          }
        } else {
          this.resetRuleLogic();
        }
        
        this.originalData = { ...this.formData };
      } else {
        // Add mode - reset form
        this.resetForm();
      }
      
      this.clearMessages();
      this.$refs.ruleModal.show();
    },
    
    closeModal() {
      this.$refs.ruleModal.hide();
    },
    
    resetModal() {
      this.editingRule = null;
      this.resetForm();
      this.clearMessages();
      this.originalData = null;
    },
    
    resetForm() {
      this.formData = {
        name: '',
        description: '',
        rule: '',
        query_template: '',
        threshold: '',
        is_active: true
      };
      this.resetRuleLogic();
    },
    
    resetRuleLogic() {
      this.ruleLogic = {
        mode: 'prior_window',
        window: null,
        compare_to: null,
        window_offset_amount: null,
        window_offset_unit: 'months',
        operator: '',
        aggregator: ''
      };
    },

    parseLoadedRuleLogic(parsedRule) {
      const rawMode = parsedRule.mode;
      const mode =
        rawMode === 'historical' || rawMode === 'historical_window'
          ? 'historical_window'
          : 'prior_window';

      const base = {
        mode,
        window: parsedRule.window != null ? parsedRule.window : null,
        compare_to: null,
        window_offset_amount: null,
        window_offset_unit: 'months',
        operator: parsedRule.operator || '',
        aggregator: parsedRule.aggregator || ''
      };

      if (mode === 'prior_window') {
        base.compare_to =
          parsedRule.compare_to != null ? parsedRule.compare_to : null;
        return base;
      }

      const wo = parsedRule.window_offset;
      if (wo && typeof wo === 'object') {
        if (Object.prototype.hasOwnProperty.call(wo, 'years')) {
          base.window_offset_amount =
            wo.years != null ? wo.years : null;
          base.window_offset_unit = 'years';
        } else if (Object.prototype.hasOwnProperty.call(wo, 'months')) {
          base.window_offset_amount =
            wo.months != null ? wo.months : null;
          base.window_offset_unit = 'months';
        }
      }

      if (
        base.window_offset_amount == null &&
        parsedRule.compare_to != null &&
        ['months', 'years'].includes(parsedRule.compare_unit)
      ) {
        base.window_offset_amount = parsedRule.compare_to;
        base.window_offset_unit = parsedRule.compare_unit;
      }

      return base;
    },

    buildRulePayloadObject() {
      const windowNum = Number(this.ruleLogic.window);
      const base = {
        mode: this.ruleLogic.mode,
        window: windowNum,
        operator: this.ruleLogic.operator,
        aggregator: this.ruleLogic.aggregator
      };

      if (this.ruleLogic.mode === 'prior_window') {
        return {
          ...base,
          compare_to: Number(this.ruleLogic.compare_to)
        };
      }

      const amt = Number(this.ruleLogic.window_offset_amount);
      const window_offset =
        this.ruleLogic.window_offset_unit === 'years'
          ? { years: amt }
          : { months: amt };

      return {
        ...base,
        window_offset
      };
    },
    
    clearMessages() {
      this.error = null;
      this.successMessage = null;
      this.validationErrors = {};
    },
    
    async saveRule() {
      if (this.saving || !this.isFormValid) return;
      
      try {
        this.saving = true;
        this.clearMessages();
        
        const ruleLogicJson = JSON.stringify(this.buildRulePayloadObject());
        
        // Prepare data for API
        const ruleData = {
          name: this.formData.name.trim(),
          description: this.formData.description?.trim() || '',
          rule: ruleLogicJson,
          query_template: this.formData.query_template?.trim() || '',
          threshold: this.formData.threshold ? String(this.formData.threshold).trim() : '',
          is_active: this.formData.is_active
        };
        
        // Remove empty optional fields
        if (!ruleData.description) delete ruleData.description;
        
        let response;
        if (this.editingRule) {
          // Update existing rule
          response = await RulesService.updateRule(this.editingRule.id, ruleData);
        } else {
          // Create new rule
          response = await RulesService.createRule(ruleData);
        }
        
        this.successMessage = `Rule ${this.editingRule ? 'updated' : 'created'} successfully!`;
        
        // Emit success event
        this.$emit('saved', response);
        
        // Close modal after a short delay
        setTimeout(() => {
          this.closeModal();
        }, 1500);
        
      } catch (error) {
        console.error('Error saving rule:', error);
        
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          
          // Handle validation errors
          if (errorData.errors || errorData.non_field_errors) {
            this.validationErrors = errorData.errors || {};
            if (errorData.non_field_errors) {
              this.error = errorData.non_field_errors.join(', ');
            }
          } else {
            this.error = formatErrorMessage(error) || `Failed to ${this.editingRule ? 'update' : 'create'} rule. Please try again.`;
          }
        } else {
          this.error = formatErrorMessage(error) || `Failed to ${this.editingRule ? 'update' : 'create'} rule. Please try again.`;
        }
      } finally {
        this.saving = false;
      }
    },
    
    confirmDelete() {
      if (!this.editingRule) return;
      this.$refs.deleteModal.show();
    },
    
    cancelDelete() {
      this.$refs.deleteModal.hide();
    },
    
    resetDeleteModal() {
      this.deleting = false;
    },
    
    async deleteRule() {
      if (this.deleting || !this.editingRule) return;
      
      try {
        this.deleting = true;
        
        await RulesService.deleteRule(this.editingRule.id);
        
        // Emit delete event
        this.$emit('deleted', this.editingRule);
        
        // Close both modals
        this.$refs.deleteModal.hide();
        this.closeModal();
        
      } catch (error) {
        console.error('Error deleting rule:', error);
        this.error = formatErrorMessage(error) || 'Failed to delete rule. Please try again.';
        this.$refs.deleteModal.hide();
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.rule-modal-content {
  .modal-header-custom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem;
    border-bottom: 1px solid #dee2e6;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    
    .modal-title {
      margin: 0;
      font-weight: 600;
      color: #212529;
    }
    
    .modal-actions {
      display: flex;
      gap: 0.5rem;
    }
  }
  
  .modal-body-custom {
    padding: 1.5rem;
    max-height: 60%;
    overflow-y: auto;
    
    .form-group {
      margin-bottom: 1.5rem;
      
      .form-label {
        font-weight: 600;
        color: #495057;
        margin-bottom: 0.5rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        
        &.required::after {
          content: "*";
          color: #dc3545;
          margin-left: 0.25rem;
        }
        
        i {
          color: #6c757d;
          font-size: 1rem;
        }
      }
      
      .form-label-sm {
        font-weight: 600;
        color: #495057;
        margin-bottom: 0.375rem;
        font-size: 0.875rem;
        display: block;
        
        &.required::after {
          content: "*";
          color: #dc3545;
          margin-left: 0.25rem;
        }
      }
      
      .rule-logic-container {
        background: #f8f9fa;
        border-radius: 0.375rem;
        padding: 1rem;
        border: 1px solid #e9ecef;

        .rule-mode-block {
          margin-bottom: 1rem;

          .rule-mode-inline-error {
            margin-top: 0.375rem;
          }
        }

        .rule-mode-row {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem 0.75rem;

          .rule-mode-label {
            margin-bottom: 0;
          }

          .rule-mode-select {
            width: auto;
            min-width: 14rem;
            max-width: 100%;
            flex: 0 1 auto;
          }
        }

        .window-offset-row {
          gap: 0.375rem;

          .window-offset-value-input {
            min-width: 0;
            flex: 1 1 auto;
          }

          .window-offset-unit-select {
            flex: 0 0 auto;
            width: auto;
            min-width: 7rem;
          }
        }
        
        .row {
          margin: 0;
          
          .col-md-4, .col-md-3 {
            padding: 0 0.5rem;
            
            &:first-child {
              padding-left: 0;
            }
            
            &:last-child {
              padding-right: 0;
            }
          }
        }
      }
      
      .form-control {
        border-radius: 0.375rem;
        border: 1px solid #ced4da;
        padding: 0.5rem 0.75rem;
        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        
        &:focus {
          border-color: #007bff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
        
        &.code-textarea {
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.875rem;
          line-height: 1.4;
        }

        &.compact-select {
          padding: 0.375rem 0.5rem;
          font-size: 0.875rem;
          height: 34px;
        }

        &.select-open {
          border-color: #17a2b8;
          box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
        }
      }

      textarea.form-control.compact-select {
        height: auto;
        min-height: 70px;
        padding: 0.5rem 0.75rem;
        font-size: 0.9rem;
      }
      
      .custom-control {
        padding-left: 2rem;
        
        .custom-control-label {
          font-weight: 500;
          color: #495057;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          
          i {
            color: #28a745;
            font-size: 1.1rem;
          }
        }
      }
      
      .form-text {
        font-size: 0.875rem;
        margin-top: 0.25rem;
      }
    }
  }
  
  .modal-footer-custom {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1.25rem;
    border-top: 1px solid #dee2e6;
    background-color: #f8f9fa;
    
    .btn {
      border-radius: 0.375rem;
      font-weight: 500;
      padding: 0.5rem 1rem;
      transition: all 0.3s ease;
      
      &.btn-primary {
        background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
        border: none;
        
        &:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
        }
      }
      
      &.btn-secondary {
        background: #6c757d;
        border: none;
        
        &:hover:not(:disabled) {
          background: #545b62;
        }
      }
      
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }
}

.alert {
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  
  i {
    font-size: 1.1rem;
  }
}

// Delete modal styles
.text-center {
  i.la-3x {
    font-size: 3rem;
  }
}

.gap-2 {
  gap: 0.5rem;
}
</style>
